/**
 * Lumyn — Analytics Hub (Phase 3 rebuild around sessions + items)
 *
 * Tabs (in role order):
 *   Sessions       — list recent Live sessions with download-report affordance
 *   Item analysis  — banks with calibration read-out (routes to Question Banks)
 *   Trainers       — per-trainer session count + avg accuracy (org_admin+)
 *
 * Defensive design: data is read from existing endpoints we already expose;
 * deep filtering / drill-downs are intentionally deferred — Phase 3 brief
 * flags them as UAT-dependent.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { SkeletonCard } from '../components/common/SkeletonCard';
import { EmptyState } from '../components/common/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BarChart3, Flame, Library, ArrowRight, Users, Download,
  FileText, FileSpreadsheet, Activity, UserSquare2, Sparkles,
  LayoutDashboard, CalendarCheck, Award, BookOpen, Building2, ShieldAlert, Gauge,
} from 'lucide-react';
import BIFilterBar, { ALL as BI_ALL } from './analytics/BIFilterBar';
import {
  OverviewBITab, AttendanceBITab, AssessmentsBITab, LearningBITab,
  DepartmentsBITab, RiskBITab,
} from './analytics/BITabs';
import ReadinessBITab from './analytics/ReadinessBITab';
import { ROLE_LEVEL } from '../utils/constants';

const TABS = [
  { id: 'overview',    label: 'Overview',    minRole: 'trainer', icon: LayoutDashboard, bi: true },
  { id: 'attendance',  label: 'Attendance',  minRole: 'trainer', icon: CalendarCheck,   bi: true },
  { id: 'assessments', label: 'Assessments', minRole: 'trainer', icon: Award,           bi: true },
  { id: 'learning',    label: 'Learning',    minRole: 'trainer', icon: BookOpen,        bi: true },
  { id: 'departments', label: 'Departments', minRole: 'trainer', icon: Building2,       bi: true },
  { id: 'risk',        label: 'Risk',        minRole: 'trainer', icon: ShieldAlert,     bi: true },
  { id: 'readiness',   label: 'Readiness',   minRole: 'trainer', icon: Gauge,           bi: true },
  { id: 'sessions', label: 'Sessions', minRole: 'trainer', icon: Flame },
  { id: 'items',    label: 'Item analysis',    minRole: 'trainer', icon: Library },
  { id: 'trainers', label: 'Trainers', minRole: 'org_admin',       icon: UserSquare2 },
];

function iso(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function SessionsTab({ get }) {
  const [sessions, setSessions] = useState(null);
  useEffect(() => {
    get('/live/sessions').then((list) => setSessions(list || [])).catch(() => setSessions([]));
  }, [get]);
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  if (sessions == null) return <SkeletonCard />;
  if (sessions.length === 0) {
    return (
      <EmptyState
        icon={Flame}
        title={'No sessions yet'}
        description={'Run a Live session from Content → Run live to see it here.'}
      />
    );
  }
  return (
    <div className="space-y-2" data-testid="sessions-tab">
      {sessions.map((s) => {
        const ended = s.status === 'ended';
        return (
          <Card key={s.session_id} className="hover:border-[var(--lumyn-content)] transition">
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{s.quiz_title}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    ended ? 'bg-muted text-muted-foreground' : 'bg-green-500/20 text-green-700 dark:text-green-400'
                  }`}>
                    {s.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3">
                  <span className="font-mono">{s.ref_code}</span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3" />{s.participant_count}
                  </span>
                  <span>{iso(s.created_at)}</span>
                </div>
              </div>
              {ended && (
                <div className="flex items-center gap-1.5">
                  <a
                    href={`${apiBase}/api/v1/live/sessions/${s.session_id}/report.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`pdf-${s.session_id}`}
                  >
                    <Button variant="outline" size="sm">
                      <FileText className="w-3.5 h-3.5 mr-1" />PDF
                    </Button>
                  </a>
                  <a
                    href={`${apiBase}/api/v1/live/sessions/${s.session_id}/report.xlsx`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`xlsx-${s.session_id}`}
                  >
                    <Button variant="outline" size="sm">
                      <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
                    </Button>
                  </a>
                </div>
              )}
              <Link to={`/editor/live?session=${s.session_id}`}>
                <Button variant="ghost" size="sm">
                  Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ItemsTab({ get }) {
  const [banks, setBanks] = useState(null);
  useEffect(() => {
    get('/question-banks?limit=50').then((r) => setBanks(r.banks || [])).catch(() => setBanks([]));
  }, [get]);
  if (banks == null) return <SkeletonCard />;
  if (banks.length === 0) {
    return (
      <EmptyState
        icon={Library}
        title={'No banks yet'}
        description={'Create a question bank to start accumulating item-analysis signal.'}
      />
    );
  }
  // Bank-usage rollup — surfaces "Used in N quizzes" so trainers can spot
  // which banks are pulling weight vs. shelfware. Sort by usage desc so the
  // heavy hitters bubble to the top; ties fall back to alphabetical.
  const ranked = [...banks].sort((a, b) => {
    const ua = a.usage_count || 0;
    const ub = b.usage_count || 0;
    if (ub !== ua) return ub - ua;
    return (a.title || '').localeCompare(b.title || '');
  });
  const totalUsage = ranked.reduce((acc, b) => acc + (b.usage_count || 0), 0);
  return (
    <div className="space-y-2" data-testid="items-tab">
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Activity className="w-3.5 h-3.5" />
        Item-analysis pips appear inside each bank once items have ≥ 5 attempts. Open a bank to audit.
      </p>
      <Card className="bg-[var(--lumyn-banks)]/5 border-[var(--lumyn-banks)]/30" data-testid="bank-usage-summary">
        <CardContent className="p-3 text-xs flex items-center gap-3 flex-wrap">
          <Library className="w-4 h-4 text-[var(--lumyn-banks)]" />
          <span className="font-medium text-foreground">{ranked.length}</span>
          <span className="text-muted-foreground">bank{ranked.length === 1 ? '' : 's'}</span>
          <span className="text-muted-foreground">·</span>
          <span className="font-medium text-foreground">{totalUsage}</span>
          <span className="text-muted-foreground">quiz reference{totalUsage === 1 ? '' : 's'} across all banks</span>
        </CardContent>
      </Card>
      {ranked.map((b) => {
        const used = b.usage_count || 0;
        return (
          <Card key={b.bank_id} className="hover:border-[var(--lumyn-analytics)] hover:shadow-md hover:shadow-[var(--lumyn-analytics)]/10 transition">
            <CardContent className="p-4 flex items-center gap-3">
              <Library className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{b.title}</div>
                <div className="text-xs text-muted-foreground">
                  {b.item_count ?? 0} items · {b.lifecycle_status}
                </div>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] tabular-nums ${
                  used > 0
                    ? 'bg-[var(--lumyn-banks)]/15 text-[var(--lumyn-banks)] border border-[var(--lumyn-banks)]/40'
                    : 'bg-muted text-muted-foreground border border-border'
                }`}
                data-testid={`bank-usage-${b.bank_id}`}
                title={used > 0 ? `Sampled in ${used} quiz/quizzes` : 'Not yet referenced by any quiz'}
              >
                <Activity className="w-3 h-3" />
                {used > 0 ? `Used in ${used}` : 'Unused'}
              </span>
              <Link to={`/editor/banks/${b.bank_id}`}>
                <Button variant="ghost" size="sm">
                  Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TrainersTab({ get, post }) {
  const [rows, setRows] = useState(null);
  const [recs, setRecs] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    get('/analytics/trainers/performance')
      .then((r) => setRows(r.trainers || []))
      .catch(() => setRows([]));
  }, [get]);

  const coach = async (uid) => {
    setBusy(uid);
    try {
      const r = await post(`/analytics/trainers/${uid}/recommendations`, {});
      setRecs((p) => ({ ...p, [uid]: r.recommendations || r.error || 'No recommendations available.' }));
    } catch {
      setRecs((p) => ({ ...p, [uid]: 'AI generation failed.' }));
    } finally { setBusy(null); }
  };

  if (rows == null) return <SkeletonCard />;
  if (rows.length === 0) {
    return <EmptyState icon={UserSquare2} title={'No trainer activity yet'} description={'Once trainers run sessions or present decks, their performance appears here.'} />;
  }
  const STAT = (l, v) => (
    <div key={l} className="rounded bg-muted/40 py-2">
      <div className="text-base font-display font-bold tabular-nums">{v}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{l}</div>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="trainers-tab">
      {rows.map((r) => (
        <Card key={r.user_id} data-testid={`trainer-card-${r.user_id}`}>
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{r.name}</div>
                {r.role && <div className="text-[11px] text-muted-foreground">{r.role}</div>}
              </div>
              <button
                onClick={() => coach(r.user_id)}
                disabled={busy === r.user_id}
                className="text-xs px-3 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5 shrink-0"
                data-testid={`trainer-coach-${r.user_id}`}
              >
                <Sparkles className="w-3.5 h-3.5" />{busy === r.user_id ? 'Thinking…' : 'AI coaching'}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
              {STAT('Sessions', r.sessions_run)}
              {STAT('Decks', r.decks_presented)}
              {STAT('Trainees', r.trainees_count)}
              {STAT('Attendance', r.avg_attendance != null ? `${r.avg_attendance}%` : '—')}
              {STAT('Assessment', r.avg_assessment != null ? `${r.avg_assessment}%` : '—')}
            </div>
            {recs[r.user_id] && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm whitespace-pre-wrap text-slate-700" data-testid={`trainer-recs-${r.user_id}`}>
                {recs[r.user_id]}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function AnalyticsHubPage() {
  const { get, post } = useApi();
  const { user, impersonatingRole } = useAuth();
  const role = impersonatingRole || user?.role;
  const level = ROLE_LEVEL[role] || 0;
  const visibleTabs = TABS.filter((tab) => level >= (ROLE_LEVEL[tab.minRole] || 99));
  const [tab, setTab] = useState(() => visibleTabs[0]?.id || 'sessions');
  const [biFilters, setBiFilters] = useState({
    departmentId: BI_ALL, batchId: BI_ALL, dateFrom: '', dateTo: '',
  });
  const isBI = TABS.find(t => t.id === tab)?.bi;

  return (
    <div className="space-y-5 relative" data-testid="analytics-hub">
      <div aria-hidden className="page-blob bg-[var(--lumyn-analytics)]" />
      <div className="relative z-10 space-y-5">
      <div className="space-y-1.5">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-analytics)]/10 text-[var(--lumyn-analytics)] text-[11px] font-semibold uppercase tracking-[0.14em]">
          <BarChart3 className="w-3.5 h-3.5" />Analytics
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{'Analytics'}</h1>
        <p className="text-sm text-muted-foreground">Sessions, item analysis, trainer performance — all in one boardroom.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {visibleTabs.map((tabDef) => {
          const Icon = tabDef.icon;
          const active = tab === tabDef.id;
          return (
            <button
              key={tabDef.id}
              onClick={() => setTab(tabDef.id)}
              data-testid={`tab-${tabDef.id}`}
              className={`relative inline-flex items-center gap-1.5 px-3 py-2.5 text-sm transition-colors ${
                active ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className={`w-4 h-4 ${active ? 'text-[var(--lumyn-analytics)]' : ''}`} />
              {tabDef.label}
              {active && (
                <span aria-hidden className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--lumyn-analytics)] rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {isBI && <BIFilterBar filters={biFilters} onChange={setBiFilters} />}

      {tab === 'overview' && <OverviewBITab filters={biFilters} />}
      {tab === 'attendance' && <AttendanceBITab filters={biFilters} />}
      {tab === 'assessments' && <AssessmentsBITab filters={biFilters} />}
      {tab === 'learning' && <LearningBITab filters={biFilters} />}
      {tab === 'departments' && <DepartmentsBITab filters={biFilters} />}
      {tab === 'risk' && <RiskBITab filters={biFilters} />}
      {tab === 'readiness' && <ReadinessBITab filters={biFilters} />}
      {tab === 'sessions' && <SessionsTab get={get} />}
      {tab === 'items' && <ItemsTab get={get} />}
      {tab === 'trainers' && <TrainersTab get={get} post={post} />}
      </div>
    </div>
  );
}
